import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { supabase } from "../../supabaseClient";
import { haptic, hideKeyboard } from "../../utils/nativeBridge";

export function SignInScreen({onBack,onSuccess,notice}){
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [error,setError]=useState("");
  // Surface a one-time notice (e.g. "your session expired") in the error slot,
  // then clear the persisted flag so it doesn't re-show on later visits.
  useEffect(()=>{ if(notice){ setError(notice); try{ localStorage.removeItem("bam_session_expired"); }catch{} } },[notice]);
  const [loading,setLoading]=useState(false);
  const [showPw,setShowPw]=useState(false);
  const [forgotOpen,setForgotOpen]=useState(false);
  const [forgotEmail,setForgotEmail]=useState("");
  const [forgotSending,setForgotSending]=useState(false);
  const [forgotSent,setForgotSent]=useState(false);
  const [forgotError,setForgotError]=useState("");
  const handleSignIn=async()=>{
    if(!email.trim()||!password){
      haptic("error");
      setError("Please enter your email and password.");return;
    }
    setLoading(true);setError("");
    hideKeyboard();
    try{
      const {data,error:err}=await supabase.auth.signInWithPassword({email:email.trim(),password});
      if(err) throw err;
      if(data.user){
        try{localStorage.setItem("onboardingComplete","true");localStorage.setItem("bam_auth_token","sb");}catch{}
        haptic("success");
        onSuccess(data.user);
      }
    }catch(e){
      haptic("error");
      setError(e.message||"Sign in failed. Please check your credentials.");
    }finally{setLoading(false);}
  };
  const handleForgot=async()=>{
    if(!forgotEmail.trim()){setForgotError("Please enter your email.");return;}
    setForgotSending(true);setForgotError("");
    try{
      // Use a query param, not a hash route. Supabase appends
      // `#access_token=…&type=recovery` to the redirect URL; if the redirect
      // already ends in `#/reset`, the result is `…/#/reset#access_token=…`
      // (two `#`s) and the browser only treats the first one as the fragment,
      // so Supabase's auto-detect can't find the tokens. Query param keeps
      // the hash slot free for Supabase. App.js routes `?recovery=1` to
      // ResetPasswordPage.
      const redirectTo=`${window.location.origin}/?recovery=1`;
      const {error:err}=await supabase.auth.resetPasswordForEmail(forgotEmail.trim(),{redirectTo});
      if(err) throw err;
      setForgotSent(true);
    }catch(e){
      setForgotError(e.message||"Couldn't send reset email.");
    }finally{setForgotSending(false);}
  };
  return (
    <div style={{position:"fixed",inset:0,zIndex:10000,background:"#080808",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{textAlign:"center",padding:40,width:"100%",maxWidth:420}}>
        <img src={BAM_LOGO_PNG} alt="BAM" style={{width:56,height:70,objectFit:"contain",marginBottom:20,filter:"drop-shadow(0 0 20px rgba(226,221,159,0.3))"}}/>
        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:38,color:"#F5F0E8",letterSpacing:2,marginBottom:8}}>WELCOME BACK</div>
        <div style={{fontSize:14,color:"#9A9488",marginBottom:32}}>Sign in to your coaching platform</div>
        <div style={{textAlign:"left"}}>
          <div style={{fontSize:13,color:"#9A9488",marginBottom:6}}>Email</div>
          <input type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter")handleSignIn();}}
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            style={{width:"100%",padding:"14px 18px",borderRadius:10,fontSize:16,fontFamily:"'DM Sans',sans-serif",background:"#0E0E0E",color:"#F5F0E8",border:"1px solid rgba(255,255,255,0.10)",marginBottom:14,boxSizing:"border-box"}}/>
          <div style={{fontSize:13,color:"#9A9488",marginBottom:6}}>Password</div>
          <div style={{position:"relative",marginBottom:14}}>
            <input type={showPw?"text":"password"} placeholder="Your password" value={password} onChange={e=>setPassword(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter")handleSignIn();}}
              autoComplete="current-password"
              enterKeyHint="go"
              style={{width:"100%",padding:"14px 48px 14px 18px",borderRadius:10,fontSize:16,fontFamily:"'DM Sans',sans-serif",background:"#0E0E0E",color:"#F5F0E8",border:"1px solid rgba(255,255,255,0.10)",boxSizing:"border-box"}}/>
            <button type="button" onClick={()=>setShowPw(v=>!v)} aria-label={showPw?"Hide password":"Show password"} className="btn"
              style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"#9A9488",cursor:"pointer",padding:8,display:"flex"}}>
              {showPw?<EyeOff size={17}/>:<Eye size={17}/>}
            </button>
          </div>
          {error&&<div style={{fontSize:13,color:"#E06060",marginBottom:14,padding:"10px 14px",borderRadius:8,background:"rgba(224,96,96,0.1)",border:"1px solid rgba(224,96,96,0.3)"}}>{error}</div>}
        </div>
        <button onClick={handleSignIn} disabled={loading}
          style={{width:"100%",padding:"16px",borderRadius:10,fontSize:15,fontWeight:800,letterSpacing:1.5,
            background:loading?"rgba(255,255,255,0.08)":GOLD,color:loading?"#666":"#111",border:"none",
            fontFamily:"'Bebas Neue',sans-serif",cursor:loading?"not-allowed":"pointer",marginTop:8}}>
          {loading?"SIGNING IN...":"SIGN IN"}
        </button>
        <div onClick={()=>{setForgotOpen(true);setForgotEmail(email);setForgotError("");setForgotSent(false);}}
          className="btn" style={{marginTop:14,fontSize:12,color:"#9A9488",cursor:"pointer",textAlign:"center"}}>
          Forgot password?
        </div>
        {/* "Sign up" link is hidden on native (Capacitor) — onBack is
            null in that case. App Store rules forbid promoting external
            sign-up flows from a "reader" app. New coaches sign up on the
            web at platform.byanymeanscoaches.com. */}
        {onBack && (
          <div onClick={onBack} className="btn" style={{marginTop:14,fontSize:13,color:"#9A9488",cursor:"pointer"}}>
            Don't have an account? <span style={{color:GOLD,fontWeight:700}}>Sign up</span>
          </div>
        )}
      </div>

      {/* Forgot Password Modal */}
      {forgotOpen && (
        <div onClick={()=>setForgotOpen(false)}
          style={{position:"fixed",inset:0,zIndex:10001,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(8px)",
            display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif"}}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:"#111",border:"1px solid rgba(255,255,255,0.1)",borderRadius:14,
              width:"min(420px, 92vw)",padding:"28px 26px",position:"relative"}}>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:"#F5F0E8",letterSpacing:2,marginBottom:6}}>RESET PASSWORD</div>
            {forgotSent ? (
              <>
                <p style={{fontSize:13,color:"#9A9488",lineHeight:1.6,marginBottom:20}}>
                  Check your inbox at <strong style={{color:"#F5F0E8"}}>{forgotEmail}</strong> for a reset link.
                </p>
                <button onClick={()=>setForgotOpen(false)}
                  style={{width:"100%",padding:"12px",borderRadius:10,fontSize:14,fontWeight:700,background:GOLD,color:"#111",
                    border:"none",fontFamily:"'Bebas Neue',sans-serif",letterSpacing:1.5,cursor:"pointer"}}>
                  CLOSE
                </button>
              </>
            ) : (
              <>
                <p style={{fontSize:13,color:"#9A9488",lineHeight:1.6,marginBottom:16}}>
                  Enter your email and we'll send you a link to reset your password.
                </p>
                <input type="email" placeholder="your@email.com" value={forgotEmail}
                  onChange={e=>setForgotEmail(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter")handleForgot();}}
                  inputMode="email" autoComplete="email" autoCapitalize="off" autoCorrect="off" spellCheck={false} enterKeyHint="send"
                  style={{width:"100%",padding:"12px 14px",borderRadius:10,fontSize:16,fontFamily:"'DM Sans',sans-serif",
                    background:"#0E0E0E",color:"#F5F0E8",border:"1px solid rgba(255,255,255,0.10)",marginBottom:12,boxSizing:"border-box"}}/>
                {forgotError && <div style={{fontSize:12,color:"#E06060",marginBottom:12}}>{forgotError}</div>}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setForgotOpen(false)}
                    style={{flex:1,padding:"12px",borderRadius:10,fontSize:13,fontWeight:600,background:"transparent",color:"#9A9488",
                      border:"1px solid rgba(255,255,255,0.1)",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                    Cancel
                  </button>
                  <button onClick={handleForgot} disabled={forgotSending}
                    style={{flex:2,padding:"12px",borderRadius:10,fontSize:14,fontWeight:700,
                      background:forgotSending?"rgba(255,255,255,0.08)":GOLD,color:forgotSending?"#666":"#111",
                      border:"none",fontFamily:"'Bebas Neue',sans-serif",letterSpacing:1.5,
                      cursor:forgotSending?"not-allowed":"pointer"}}>
                    {forgotSending?"SENDING...":"SEND RESET LINK"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
